import './quick-access.scss';
import React, { useMemo, useState } from "react";
import { LocalCharacterInitializer } from "../../../../../entities/characters/Character";
import { ThemedCheckbox } from "../../../../inputs/checkbox/ThemedCheckbox";
import { RollTool } from "../roll-tool";
import { Loader } from '../../../../theming/loader/Loader';
import { useAppDispatch, useAppSelector } from '../../../../../store/configureStore';
import { CharacterPropertyActions } from '../../../../../store/stores/collection/characters/actions/CharacterPropertyActions';
import { ThemedButton } from '../../../../inputs/buttons/ThemedButton';
import { BreakdownWindow } from '../../../../theming/breakdown-window';
import { AdventureActions } from '../../../../../store/stores/adventures/AdventureStore.Actions';
import { Link } from 'react-router-dom';
import { Die } from '../../../../figures/Die';

type Props = { character: LocalCharacterInitializer }

export const SheetQuickAccess: React.FC<Props> = (props: Props) => {
    const { character } = props;
    const [menuOpen, setMenuOpen] = useState(false);
    const characters = useAppSelector(app => app.character.characters);
    const { adventures, modifyingCharacterOnAdventure } = useAppSelector(app => app.adventure);
    const adventure = useMemo(() => adventures.find(a => a.characterIds.contains(character.id)),
        [adventures, character.id]);
    const { enforceRules, pendingProperties } = character;
    const dispatch = useAppDispatch();
    const enforceRulesPending = pendingProperties["enforceRules"];
    const setChecked = (selected: boolean) => dispatch(CharacterPropertyActions.setRuleEnforcement(character, selected));
    return (
        <div className="character-quick-access">
            <ThemedButton onClick={() => setMenuOpen(true)}>Character Settings</ThemedButton>
            {menuOpen && (
                <BreakdownWindow close={() => setMenuOpen(false)} heading={<h1>Character Settings</h1>}>
                    <div className="strict-toggle" title="Indicates if the rules will be strictly enforced, or if you want a freeform character sheet.">
                        <ThemedCheckbox checked={enforceRules} disabled={enforceRulesPending} setChecked={setChecked} />
                        <label className="mid-line">Enforce Rules?</label>
                        {enforceRulesPending && <Loader size="S" />}
                    </div>
                    
                    <div className='panel' style={{marginTop: "10px"}}>
                        <h3 style={{marginTop: "0", marginBottom: "6px"}}>Adventures</h3>
                        {modifyingCharacterOnAdventure && <Loader />}
                        {adventure && !modifyingCharacterOnAdventure && (
                            <>
                                {character.name} is currently assigned to <label className='standout'>{adventure.name}</label>.
                                <br />
                                <ThemedButton onClick={() => {
                                    if (confirm(`Are you sure you want to remove ${character.name} from ${adventure.name}?`)) {
                                        dispatch(AdventureActions.removeCharacterFromAdventure(adventure, character));
                                    }
                                }}>Remove from {adventure.name}</ThemedButton>
                            </>
                        )}

                        {!adventure && !modifyingCharacterOnAdventure && (
                            <>
                                <label className='standout'>{character.name}</label> is not currently assigned to any adventures.
                                {adventures.length > 0 && (<>
                                    <ul>
                                        {adventures.map(a => {
                                            const disabled = characters.map(c => c.id).intersection(a.characterIds).length > 0;
                                            return (
                                                <li key={a.id} style={{marginTop: "4px"}}>
                                                    {a.name} <ThemedButton disabled={disabled} onClick={() => dispatch(AdventureActions.addCharacterToAdventure(a, character))}>Assign</ThemedButton>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </>)}
                                {adventures.length == 0 && (<>
                                    &nbsp; You aren't a member of any adventures yet, you can either join an adventure with an invitation link or you can <Link to="/Adventures/Create"><ThemedButton>Create Your Own Adventure</ThemedButton></Link>
                                </>)}
                            </>
                        )}
                    </div>
                </BreakdownWindow>
            )}

            {adventure && 
                <div style={{flex: "1 0 0", display: "flex", justifyContent: "flex-end"}}>
                    <div style={{marginRight: "6px"}}>
                        <label className="standout">Communal Dice</label>
                        <br />
                        <div className='perfect-center'>
                            <Die>{adventure.communalDice[0]}</Die>
                            <Die>{adventure.communalDice[1]}</Die>
                        </div>
                    </div>
                    <RollTool character={character} />
                </div>
            }
        </div>
    )
}